import { useEffect, useState, useMemo } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useUpdateMeMutation } from '../../store/authApi';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';

// Returns the editable fields specific to this user_type.
// `client_id` for doctors / lab_partners is shown separately as a
// locked field (assigned by IT Dose / admin, never editable here),
// so it's NOT in this list.
const fieldsForType = (type) => {
  switch (type) {
    case 'partner':
      return [
        { key: 'company_name', label: 'Hospital / Clinic' },
      ];
    case 'organization':
      return [
        { key: 'company_name', label: 'Company name' },
        { key: 'designation', label: 'Designation' },
      ];
    case 'hospital':
      return [
        { key: 'lab_name', label: 'Lab name' },
        { key: 'lab_license_number', label: 'License number' },
      ];
    case 'patient':
    default:
      return [
        { key: 'age', label: 'Age', type: 'number' },
        {
          key: 'gender',
          label: 'Gender',
          select: true,
          options: [
            { value: '', label: 'Prefer not to say' },
            { value: 'male', label: 'Male' },
            { value: 'female', label: 'Female' },
            { value: 'other', label: 'Other' },
          ],
        },
      ];
  }
};

const formatLabel = (s) => String(s || '').replace(/_/g, ' ');

const ProfileDrawer = ({ open, onClose }) => {
  const { user, updateUser } = useAuth();
  const { showToast } = useUI();
  const [updateMe, { isLoading: saving }] = useUpdateMeMutation();

  const [editing, setEditing] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [form, setForm] = useState(() => buildForm(user));

  useEffect(() => {
    setForm(buildForm(user));
    setEditing(false);
    setErrMsg('');
  }, [user, open]);

  const typeFields = useMemo(() => fieldsForType(user?.user_type), [user?.user_type]);

  const initials = (user?.name || '')
    .split(' ').filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase()).join('');

  const onChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSave = async () => {
    setErrMsg('');
    if (!form.name?.trim()) return setErrMsg('Name cannot be empty');

    const payload = { name: form.name.trim() };
    typeFields.forEach(({ key }) => {
      payload[key] = form[key] === '' ? null : form[key];
    });

    try {
      const res = await updateMe(payload).unwrap();
      updateUser(res.user);
      setEditing(false);
      showToast('Profile updated');
    } catch (err) {
      setErrMsg(err?.data?.message || 'Could not update profile');
    }
  };

  if (!user) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          bgcolor: 'var(--cream)',
          borderLeft: '1px solid var(--border-l)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid var(--border-l)',
          bgcolor: '#fff',
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>
          {editing ? 'Edit profile' : 'My profile'}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ p: { xs: 2.5, sm: 3 }, flex: 1, overflowY: 'auto' }}>
        {/* Identity card */}
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1px solid var(--border-l)',
            borderRadius: '14px',
            p: 2.5,
            mb: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: 'var(--sage)',
              color: '#fff',
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.2 }}
              noWrap
            >
              {user.name}
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--sage)',
                mt: 0.5,
              }}
            >
              {formatLabel(user.user_type)}
            </Typography>
          </Box>
        </Box>

        {errMsg && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
            {errMsg}
          </Alert>
        )}

        {!editing ? (
          // ─── View mode ───
          <Stack spacing={1.5}>
            <KV label="Mobile" value={user.phone} />
            {/* UHID — assigned by the eDOS system via the patient-
                sync webhook. Display-only on this side; the source
                of truth lives on eDOS so we never expose it for edit. */}
            {user.uhid && (
              <KV label="UHID" value={user.uhid} />
            )}
            {/* Client ID — assigned by the IT Dose CentreMaster sync
                for doctors / lab partners. Display-only here for the
                same reason as UHID; managed in IT Dose. */}
            {user.client_id && (
              <KV label="Client ID" value={user.client_id} />
            )}
            <Divider sx={{ my: 0.5 }} />
            {typeFields.map((f) => (
              <KV
                key={f.key}
                label={f.label}
                value={user[f.key] || '—'}
              />
            ))}
            <Box sx={{ mt: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setEditing(true)}
                startIcon={<EditOutlinedIcon />}
                sx={{
                  borderRadius: 100,
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1,
                }}
              >
                Edit profile
              </Button>
            </Box>
          </Stack>
        ) : (
          // ─── Edit mode ───
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Full name"
              value={form.name || ''}
              onChange={onChange('name')}
              disabled={saving}
            />
            <TextField
              fullWidth
              label="Mobile (locked)"
              value={user.phone}
              disabled
              helperText="Phone number is your login — contact support to change."
            />
            {/* UHID is assigned by the eDOS system; never editable here. */}
            {user.uhid && (
              <TextField
                fullWidth
                label="UHID (locked)"
                value={user.uhid}
                disabled
                helperText="Assigned by your lab — managed in the eDOS system."
              />
            )}
            {/* Client ID is assigned by the IT Dose admin; never
                editable here. Shown to doctors / lab partners so they
                can copy it for support requests. */}
            {user.client_id && (
              <TextField
                fullWidth
                label="Client ID (locked)"
                value={user.client_id}
                disabled
                helperText="Assigned by admin — managed in IT Dose."
              />
            )}
            <Divider />
            {typeFields.map((f) =>
              f.select ? (
                <TextField
                  key={f.key}
                  select
                  fullWidth
                  label={f.label}
                  value={form[f.key] ?? ''}
                  onChange={onChange(f.key)}
                  disabled={saving}
                >
                  {f.options.map((o) => (
                    <MenuItem key={o.value || 'none'} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  key={f.key}
                  fullWidth
                  type={f.type || 'text'}
                  label={f.label}
                  value={form[f.key] ?? ''}
                  onChange={onChange(f.key)}
                  disabled={saving}
                />
              )
            )}

            <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setEditing(false)}
                disabled={saving}
                sx={{ borderRadius: 100, textTransform: 'none', fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={onSave}
                disabled={saving}
                sx={{ borderRadius: 100, textTransform: 'none', fontWeight: 700 }}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};

const KV = ({ label, value }) => (
  <Box
    sx={{
      bgcolor: '#fff',
      border: '1px solid var(--border-l)',
      borderRadius: '10px',
      px: 2,
      py: 1.25,
    }}
  >
    <Typography
      sx={{
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: 'var(--stone)',
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: 14, color: 'var(--ink)', wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

const buildForm = (u) =>
  u
    ? {
        name: u.name || '',
        age: u.age ?? '',
        gender: u.gender || '',
        specialty: u.specialty || '',
        registration_number: u.registration_number || '',
        hospital: u.hospital || '',
        company_name: u.company_name || '',
        designation: u.designation || '',
        lab_name: u.lab_name || '',
        lab_license_number: u.lab_license_number || '',
      }
    : {};

export default ProfileDrawer;
